import {COMPANY_KEYS} from "@/api/companyApi";
import {useUpdateUserProfileMutation} from "@/api/userApi";
import Button from "@/components/Common/Button";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useSettingsDirty} from "@/hooks/useSettingsNavigation";
import {useSelectedCompanyId} from "@/hooks/useUserHooks";
import UserAvatar from "@/pages/profile/UserAvatar";
import CredentialsStep from "@/pages/registerCompany/steps/CredentialsStep";
import {queryClient} from "@/queryClient";
import {PERMISSION_LABEL_KEYS} from "@/store/constants";
import useRegistrationStore from "@/store/registration";
import useUserStore from "@/store/user";
import {getUserPermission} from "@/utils/permissionUtils";
import {useEffect, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useShallow} from "zustand/shallow";

const AccountProfilePane: FC = () => {
  const {t} = useTranslation(["multi_account", "settings"]);
  const user = useUserStore(state => state.user);
  const setUserData = useUserStore(state => state.setUserData);
  const {refetch: refetchUser} = useFetchUser();
  const selectedCompanyId = useSelectedCompanyId();
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const memberships = user.sub_brands ?? [];
  const isOwner = memberships.length === 1 && getUserPermission(memberships[0]) === "ADMIN";

  const setValue = useRegistrationStore(state => state.setValue);
  const validateCredentials = useRegistrationStore(state => state.validateCredentials);
  const [firstName, lastName] = useRegistrationStore(useShallow(state => [state.firstName, state.lastName]));
  useSettingsDirty(!!profilePicture || firstName !== (user.first_name ?? "") || lastName !== (user.last_name ?? ""));

  useEffect(() => {
    setValue("isEmailPrefilled", true);
    setValue("email", user.email ?? "");
    setValue("firstName", user.first_name ?? "");
    setValue("lastName", user.last_name ?? "");
  }, [user.email, user.first_name, user.last_name]);

  const {mutate: updateUser, isPending} = useUpdateUserProfileMutation({
    onSuccessCallback: () => {
      setUserData({...user, first_name: firstName, last_name: lastName});
      setProfilePicture(null);
      queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompanyMembers(selectedCompanyId!)});
      refetchUser();
    },
    userId: user.id,
  });

  return (
    <div className="flex flex-col gap-4">
      <span className="text-neutral-black text-lg leading-none font-bold">{t("settings:profile")}</span>
      <div className="flex flex-col items-center gap-3">
        <UserAvatar defaultAvatar={user.profile_picture} handleUpload={setProfilePicture} />
        {isOwner && (
          <div className="flex flex-col items-center gap-1">
            <span className="text-neutral-grey-dark text-xs font-medium">{t("multi_account:permission_type")}</span>
            <span className="border-neutral-grey-light-dark text-neutral-black w-fit rounded-md border px-3 py-1 text-sm">
              {t(PERMISSION_LABEL_KEYS.ADMIN)}
            </span>
          </div>
        )}
      </div>
      <CredentialsStep
        className="gap-3"
        fieldClassName="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
        labelClassName="text-neutral-black text-sm font-medium"
        showCompanyName={false}
        showOnboardingCta={false}
      />
      <Button
        buttonStyle="Gradient"
        className="h-10 w-fit px-5"
        isDisabled={!firstName || !lastName || isPending}
        isLoading={isPending}
        onClick={() => validateCredentials() && updateUser({firstName, lastName, profilePicture})}>
        {t("multi_account:save_changes")}
      </Button>
    </div>
  );
};

export default AccountProfilePane;
